import { AccountCircle, ChevronForward, Info } from '@project-lary/react-material-symbols';
import { useInfoPanelDetails } from 'gcs-common/clients/api/entities/channels/channel-api';
import { LastOnlineStatusBadge } from 'gcs-common/components/LastOnlineStatusBadge/LastOnlineStatusBadge';
import { CHAT_ROUTES } from 'gcs-common/constants/routes';
import { RIGHT_BAR_TABS } from 'gcs-common/constants/uiFlags';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'gcs-common/design-system/ui/accordion';
import { Badge } from 'gcs-common/design-system/ui/badge';
import { getMainContactId } from 'gcs-common/slices/channel/channelSelectors';
import { getCurrentUserId } from 'gcs-common/slices/currentUser/currentUserSelectors';
import { getIsMobile } from 'gcs-common/slices/init/initSelectors';
import { currentUserIdSelected } from 'gcs-common/slices/memberDetails/memberDetailsSlice';
import { setRightBarTab } from 'gcs-common/slices/uiState/uiStateSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import ProfileImage, { ProfileImageSize } from './ProfileImage';

export interface ContactItemProps {
  userId: string;
  channelId: string;
  showInfoIcon?: boolean;
  hideStatus?: boolean;
  agentView: boolean;
  showingAllowedAgents: boolean;
  isLast: boolean;
}
const ContactItem = ({ userId, channelId, isLast, ...props }: ContactItemProps) => {
  const navigate = useNavigate();
  const isMobile = useSelector(getIsMobile);
  const currentUserId = useSelector(getCurrentUserId);
  const { data } = useInfoPanelDetails(channelId);
  const allowedAgents = (data?.assignedAgentUsers ?? []).concat(data?.assignedTeamUsers ?? []);

  const user = props.showingAllowedAgents
    ? allowedAgents.find((user) => user.id === userId)
    : data?.users?.find((user) => user.id === userId);
  const mainContactId = useSelector(getMainContactId(channelId));
  const isMainContact = mainContactId === userId;

  const { routeChannelId: selectedChannelId = '' } = useParams<{ routeChannelId: string }>();

  const dispatch = useDispatch();
  const openUserProfile = (userId: string) => {
    if (currentUserId === userId) return;
    // On mobile we navigate to the profile details page
    if (isMobile) {
      navigate(CHAT_ROUTES.USER_PROFILE(selectedChannelId, userId));
      return;
    }
    // On web we configure the right bar
    dispatch(setRightBarTab({ rightBarTab: RIGHT_BAR_TABS.MEMBER_DETAILS }));
    dispatch(currentUserIdSelected({ userId }));
  };

  if (!user) {
    return null;
  }

  const children = (
    <div className="flex h-16 flex-row items-center justify-self-stretch pl-3">
      <div className="flex items-center self-stretch pr-2">
        <ProfileImage imageUrl={user?.imageUrl} size={ProfileImageSize.SMALL} isRounded={true} />
      </div>
      <div
        className={`flex h-full w-full flex-row items-center border-0 ${!isLast ? 'border-neutral-6 border-b-thin border-solid' : ''}`}
      >
        <div className="flex shrink-0 grow flex-col items-start justify-center self-stretch justify-self-start">
          <div className="flex flex-row items-center space-x-2">
            <div> {user?.friendlyName} </div>
            {props.agentView && isMainContact && (
              <Badge variant="soft_accent" type="text">
                Hauptkontakt
              </Badge>
            )}
          </div>
          {props.agentView && !props.hideStatus && currentUserId !== userId && (
            <LastOnlineStatusBadge userStatus={user.status} />
          )}
        </div>
        <div className="flex space-x-2 justify-self-end pr-2">
          {props.agentView && props.showInfoIcon && (
            <div>
              <div className="self-center text-neutral-11 text-sm"> Detail </div>
              <Info className="h-6 w-6 text-neutral-alpha-11" />
            </div>
          )}
          {currentUserId !== userId && <ChevronForward className="h-6 w-6 text-neutral-11" />}
        </div>
      </div>
    </div>
  );

  if (currentUserId === userId) {
    return <div className="block w-full">{children}</div>;
  }

  return (
    <button
      type="button"
      className="block w-full hover:bg-neutral-4 active:bg-neutral-5"
      onClick={() => openUserProfile(userId)}
    >
      {children}
    </button>
  );
};

export interface ContactListProps {
  listTitle: string;
  listSecondTitle?: string;
  contactIds: string[];
  channelId: string;
  showInfoIcon?: boolean;
  hideStatus?: boolean;
  agentView: boolean;
}

export const ContactList = (props: ContactListProps) => {
  return (
    <div className="flex flex-col pt-4">
      <div className="flex flex-row items-center justify-between px-3 py-2">
        <div className="text-base-medium">{props.listTitle}</div>
        <div className="text-neutral-alpha-11 text-sm">{props.listSecondTitle}</div>
      </div>
      <div className="flex flex-col overflow-hidden rounded-xl bg-white">
        {props.contactIds.map((contactId, index) => (
          <ContactItem
            key={`${props.listTitle}-${contactId}`}
            userId={contactId}
            channelId={props.channelId}
            showInfoIcon={props.showInfoIcon}
            hideStatus={props.hideStatus}
            agentView={props.agentView}
            showingAllowedAgents={false}
            isLast={index === props.contactIds.length - 1}
          />
        ))}
      </div>
    </div>
  );
};

export interface AccordionListProps {
  contactIds: string[];
  channelId: string;
  showInfoIcon?: boolean;
  hideStatus?: boolean;
  agentView: boolean;
}

export const AccordionList = (props: AccordionListProps) => {
  return (
    <Accordion className="overflow-clip rounded-xl" type="single" collapsible>
      <AccordionItem value="item-1">
        <AccordionTrigger>
          <div className="flex w-full flex-row">
            <div className="flex px-4">
              <AccountCircle className="size-6 w-6" />
            </div>
            <div className="text-ellipsis font-normal text-base text-neutral-12">{`${props.contactIds.length} Mitarbeiter können beitreten`}</div>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          {props.contactIds.map((c, index) => (
            <ContactItem
              userId={c}
              key={c}
              channelId={props.channelId}
              showInfoIcon={false}
              agentView={props.agentView}
              showingAllowedAgents={true}
              hideStatus={true}
              isLast={index === props.contactIds.length - 1}
            />
          ))}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default ContactList;
